/*
* Entity Details section component
*/

import * as React from 'react';
import * as _ from 'lodash';
import { Row } from 'react-bootstrap';

import * as AppUtilities from '../apputilities';
import { BootstrapColXl as Col } from '../bootstrap/bootstrapcolxl';
import { Formatters } from '../formatters';

export const ValColDisplayModes = {
    default: '',
    Boolean: 'boolean',
};

export class AddressSection {
    public static CreateAddressSectionFields(location: any, appTextFunction: (messagePath: string, prependMessagesPathPrefix: boolean, tokenValue: string) => string): Array<any> {
        // Returns an array of SectionFields used to display entity location/address (association/client detail views)
        let result: Array<any> = [];

        if (!location) { return result; }

        const messagePathPrefix = 'app.common.location.';

        const fieldMap = [
            { appTextPath: 'address', fieldName: 'address1', hideIfEmpty: false },
            { appTextPath: 'address2', fieldName: 'address2', hideIfEmpty: true },
            { appTextPath: 'city', fieldName: 'city', hideIfEmpty: false },
            { appTextPath: 'state', fieldName: 'stateCode', hideIfEmpty: false },
            { appTextPath: 'zip', fieldName: 'postalCode', hideIfEmpty: false },
            { appTextPath: 'country', fieldName: 'countryCode', hideIfEmpty: true },
        ];

        fieldMap.forEach(c => {
            const label = appTextFunction(`${messagePathPrefix}${c.appTextPath}`, false, '');
            const value = _.get(location, c.fieldName);

            if (value) {
                result.push(SectionField.CreateSingle(label, value));
            } else if (!c.hideIfEmpty) {
                result.push(SectionField.CreateSingle(label, value));
            }
        });

        return result;
    }
}

export class SectionField {
    public renderEditControl: any = undefined;
    public editMode: boolean = false;
    public label: string = '';
    public singleValue: any = '';
    public singleValueDisplay: any = '';

    public static CreateSingle(label: string, singleValue: any, editMode: boolean = false, renderEditControl?: (props: any) => any, singleValueDisplay: any = '') {
        return new SectionField({ label: label, singleValue: singleValue, singleValueDisplay: singleValueDisplay, editMode: editMode, renderEditControl: renderEditControl });
    }

    public constructor(init?: Partial<any>) {
        Object.assign(this, init);
    }
}

export interface IEntityDetailSectionProps {
    enabled?: boolean;
    fields: Array<SectionField>;
    labelColClassName?: string;
    labelColLg?: number;
    labelColMd?: number;
    labelColSm?: number;
    labelColXl?: number;
    labelColXs?: number;
    title?: string;
    valueColClassName?: string;
    valueColLg?: number;
    valueColMd?: number;
    valueColSm?: number;
    valueColXl?: number;
    valueColXs?: number;
    valColDisplay?: any;
}

export class EntityDetailSection extends React.Component<IEntityDetailSectionProps, any> {

    public static readonly cssClassLabels: string = 'entity-detail-label';
    public static readonly cssClassValues: string = 'entity-detail-values';
    public static readonly cssClassFieldGroupRow: string = 'entity-detail-field-group';
    public static readonly cssClassFieldGroupEditRow: string = 'entity-detail-field-group-edit';
    public static readonly cssClassSection: string = 'entity-detail-section';

    public static defaultProps = {
        enabled: true,
        labelColClassName: undefined,
        labelColLg: undefined,
        labelColMd: undefined,
        labelColSm: undefined,
        labelColXl: undefined,
        labelColXs: undefined,
        valueColClassName: undefined,
        valueColLg: undefined,
        valueColMd: undefined,
        valueColSm: undefined,
        valueColXl: undefined,
        valueColXs: undefined,
        valColDisplay: undefined,
    };

    public constructor(props: IEntityDetailSectionProps, context: any) {
        super(props, context);
    }

    public getLabelColCss(props: any, addClass: string = ''): string {
        let labelCss = ` ${addClass}`;

        const {
            labelColClassName,
            labelColLg,
            labelColMd,
            labelColSm,
            labelColXl,
            labelColXs,
        } = props;

        if (labelColXs !== undefined) { labelCss += ` col-xs-${labelColXs}`; }
        if (labelColSm !== undefined) { labelCss += ` col-sm-${labelColSm}`; }
        if (labelColMd !== undefined) { labelCss += ` col-md-${labelColMd}`; }
        if (labelColLg !== undefined) { labelCss += ` col-lg-${labelColLg}`; }
        if (labelColXl !== undefined) { labelCss += ` col-xl-${labelColXl}`; }
        if (labelColClassName !== undefined) { labelCss += ` ${labelColClassName}`; }

        return labelCss.trim();
    }

    public getValueColCss(props: any, addClass: string = ''): string {
        let valueCss = ` ${addClass}`;

        const {
            valueColClassName,
            valueColLg,
            valueColMd,
            valueColSm,
            valueColXl,
            valueColXs,
        } = props;

        if (valueColXs !== undefined) { valueCss += ` col-xs-${valueColXs}`; }
        if (valueColSm !== undefined) { valueCss += ` col-sm-${valueColSm}`; }
        if (valueColMd !== undefined) { valueCss += ` col-md-${valueColMd}`; }
        if (valueColLg !== undefined) { valueCss += ` col-lg-${valueColLg}`; }
        if (valueColXl !== undefined) { valueCss += ` col-xl-${valueColXl}`; }
        if (valueColClassName !== undefined) { valueCss += ` ${valueColClassName}`; }

        return valueCss.trim();
    }

    public render() {
        const {
            fields,
            enabled,
            title,
            labelColLg,
            labelColMd,
            labelColSm,
            labelColXl,
            labelColXs,
            valueColLg,
            valueColMd,
            valueColSm,
            valueColXl,
            valueColXs,
            valColDisplay,
            children
        } = this.props;

        if (!enabled || !AppUtilities.isArrayWithLength(fields)) { return(<div />); }

        // Iterate fields and build table
        const fieldGroups = fields.map((field, i) => {
            let fieldValue = _.get(field, 'singleValue', '');
            const fieldLabel = _.get(field, 'label', '');
            const editMode = _.get(field, 'editMode', false);
            let renderEditControl = _.get(field, 'renderEditControl');

            if (editMode && renderEditControl) {
                const labelColCss = this.getLabelColCss(this.props, EntityDetailSection.cssClassLabels);
                const valueColCss = this.getValueColCss(this.props, EntityDetailSection.cssClassValues);

                const props = {
                    rowClassName: [{'form-group': false}, `${EntityDetailSection.cssClassFieldGroupRow} ${EntityDetailSection.cssClassFieldGroupEditRow}`],
                    labelClassName: [{'col-sm-3': false}, labelColCss],
                    elementWrapperClassName: [{'col-sm-9': false}, valueColCss],
                };

                return (<div key={i}>{renderEditControl(props)}</div>);
            } else {
                switch (valColDisplay) {
                    case ValColDisplayModes.Boolean:
                        fieldValue = Formatters.getBool(field, 'singleValue');

                        // fieldValue = (_.random(0, 100) > 50) ? true : false;

                        const fieldValGlyph = (fieldValue) ? (
                            <i className="fa fa-check-square" />
                        ) : (
                            <i className="fa fa-square-o" />
                        );

                        return (
                            <Row key={i} className={EntityDetailSection.cssClassFieldGroupRow}>
                                <Col xs={labelColXs} sm={labelColSm} md={labelColMd} lg={labelColLg} xl={labelColXl} className={EntityDetailSection.cssClassLabels}>
                                    <b>{fieldLabel}</b>
                                </Col>

                                <Col xs={valueColXs} sm={valueColSm} md={valueColMd} lg={valueColLg} xl={valueColXl} className={EntityDetailSection.cssClassValues}>
                                    {fieldValue} {fieldValGlyph}
                                </Col>
                            </Row>
                        );

                    case '':
                    case undefined:
                    default:
                        return (
                            <Row key={i} className={EntityDetailSection.cssClassFieldGroupRow}>
                                <Col xs={labelColXs} sm={labelColSm} md={labelColMd} lg={labelColLg} xl={labelColXl} className={EntityDetailSection.cssClassLabels}>
                                    <b>{fieldLabel}</b>
                                </Col>

                                <Col xs={valueColXs} sm={valueColSm} md={valueColMd} lg={valueColLg} xl={valueColXl} className={EntityDetailSection.cssClassValues}>
                                    {fieldValue}
                                </Col>
                            </Row>
                        );
                }
            }
        });

        let titleDisplay;

        if (title) { titleDisplay = (<h5><b>{title}&nbsp;</b></h5>); }

        return (
            <Row className={EntityDetailSection.cssClassSection}>
                <Col xs={12}>
                    {titleDisplay}
                    {fieldGroups}
                    {children}
                </Col>
            </Row>
        );
    }
}



// WEBPACK FOOTER //
// ./src/common/entitydetail/entitydetailsection.tsx